// eslint-disable-next-line import/no-extraneous-dependencies
import { StatusType } from '@datadog/browser-logs';

/** Re-exported type-level enum of log levels (info, debug, etc.) */
export type MessageStatus = StatusType;

// Datadog derives log severity from these top-level keys; mirrored by the `logger` rule in `.eslintrc.js`.
type ReservedStatusKey = 'status' | 'severity' | 'level' | 'syslog.severity';

/**
 * Forbids the Datadog-reserved severity keys; identity when `T` has none. Checks top-level keys of a closed type
 * only (not unions, index signatures, or nested keys). The tuple wrap stops `LogContext<object>` becoming `never`.
 */
export type LogContext<T> = [Extract<keyof T, ReservedStatusKey>] extends [never]
  ? T
  : T & Partial<Record<ReservedStatusKey, never>>;

/** A concrete default implementation for a simple logger */
export abstract class Logger {
  // Guarded only via the abstract `Logger` type; concrete loggers widen `context` to `object`.
  // `addAction`/`addError` go to Datadog RUM, which does not reserve these keys.
  abstract log<T extends object>(message: string, context?: LogContext<T>, status?: MessageStatus): void;
  abstract addAction(name: string, context?: object): void;
  abstract addError(error: unknown, context?: object): void;

  debug<T extends object>(message: string, context?: LogContext<T>): void {
    this.log(message, context, 'debug');
  }

  info<T extends object>(message: string, context?: LogContext<T>): void {
    this.log(message, context, 'info');
  }

  warn<T extends object>(message: string, context?: LogContext<T>): void {
    this.log(message, context, 'warn');
  }

  error<T extends object>(message: string, context?: LogContext<T>): void {
    this.log(message, context, 'error');
  }
}
